import { useEffect } from 'react'
import { Car } from 'lucide-react'
import { useGetStatementPeriods, useGetVehiclePerformance } from '../../hooks/backend/statements'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../lib/shadcn/tabs'
import {
  type OwnerVehicleRow,
  type StatementPeriod,
  type VehiclePerformanceRow,
  formatDate,
} from '../../utils/statements'
import { OperationalStateChip, LifecycleChip, ChipLabel } from './StateChips'
import { StatementTab } from './StatementTab'
import { PerformanceTab } from './PerformanceTab'

export function VehicleDetail({
  vehicle,
  ownerName,
}: {
  vehicle: OwnerVehicleRow | null
  ownerName: string
}) {
  const periods = useGetStatementPeriods()
  const performance = useGetVehiclePerformance()
  const triggerPeriods = periods.trigger
  const triggerPerformance = performance.trigger

  useEffect(() => {
    if (vehicle) {
      triggerPeriods({ vehicle_id: vehicle.vehicle_id })
      triggerPerformance({ vehicle_id: vehicle.vehicle_id })
    }
  }, [vehicle, triggerPeriods, triggerPerformance])

  if (!vehicle) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 bg-background p-12 text-center text-sm text-muted-foreground">
        <Car className="h-8 w-8" />
        Select a vehicle to view statements and performance.
      </div>
    )
  }

  const periodRows = (periods.data as StatementPeriod[] | undefined) ?? []
  const perfRows = (performance.data as VehiclePerformanceRow[] | undefined) ?? []

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Vehicle header */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm font-semibold text-foreground">
            {vehicle.plate}
          </span>
          <span className="text-sm font-semibold text-foreground">{vehicle.vehicle_name || '—'}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <div className="flex items-center gap-1.5">
            <ChipLabel>Lifecycle</ChipLabel>
            <LifecycleChip status={vehicle.lifecycle_status} />
          </div>
          <div className="flex items-center gap-1.5">
            <ChipLabel>Operational</ChipLabel>
            <OperationalStateChip state={vehicle.operational_state} />
          </div>
          {vehicle.operational_state_as_of && (
            <span className="text-[11px] text-muted-foreground">
              as of {formatDate(vehicle.operational_state_as_of)}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <Tabs defaultValue="statements" className="w-full">
          <TabsList>
            <TabsTrigger value="statements">Statements</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>
          <TabsContent value="statements">
            <StatementTab
              periods={periodRows}
              ownerName={ownerName}
              loading={periods.loading}
              onPeriodsChanged={() => triggerPeriods({ vehicle_id: vehicle.vehicle_id }, { skipCache: true })}
            />
          </TabsContent>
          <TabsContent value="performance">
            <PerformanceTab rows={perfRows} loading={performance.loading} error={performance.error} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
